export const anatomy4Module = {
  id: "anatomy4",
  title: "Kidney Anatomy: Part 4",
  sections: [
    {
      type: "text",
      content: `<h3>Venous supply</h3>
      <p>From the peritubular capillaries or the vasa recta, the blood flows into the <b class="purple-term">interlobular veins</b>, then into the <b class="purple-term">arcuate veins</b>, then into the <b class="purple-term">interlobar veins</b> and finally into the <b class="purple-term">segmental veins</b> and then into the
       <b class="purple-term">renal vein</b>. The renal vein then drains into the inferior vena cava.</p>      
      <p>It is important to note that the renal veins are <b>located anterior to the renal arteries</b> at the hilum of the kidney. This is an important anatomical relationship to remember, especially during surgical procedures involving the kidneys, as it can help to prevent accidental injury to these structures.</p> 
       <p>Also, the <b>left renal vein is longer</b> than the right renal vein, as it has to cross over the abdominal aorta to reach the inferior vena cava. 
       The left renal vein also receives additional tributaries, such as the left gonadal vein and the left adrenal vein, which can make it more susceptible to compression by surrounding structures, 
       such as in cases of nutcracker syndrome.</p>
       <p>In <b class="purple-term">nutcracker syndrome</b> (a rare condition), the left renal vein is compressed between the abdominal aorta and the superior mesenteric artery, 
       leading to symptoms such as hematuria (blood in the urine), flank pain, and varicocele
        (enlargement of the veins in the scrotum). This can happen due to the anatomical positioning of the vessels, combined with factors such as rapid growth during adolescence, weight loss, or certain body positions that can exacerbate the compression.
        This condition can be diagnosed through imaging studies,
         such as Doppler ultrasound or CT angiography, and may require surgical intervention in severe cases.</p>
      <div class="highlight-box">
      <b>Abdominal aorta</b> => Renal artery => Segmental arteries => Interlobar arteries => Arcuate arteries 
      => Interlobular arteries => Afferent arterioles => Glomerular capillaries => Efferent arterioles => Peritubular capillaries (cortex) or Vasa recta (medulla) => 
        Interlobular veins => Arcuate veins => Interlobar veins => Segmental veins => Renal vein => <b>Inferior vena cava</b>
      </div>
      `,
      image: "/images/kidney/kidneyveins.png",
    },
    {
      type: "question",
      text: "How many renal veins are there?",
      options: [
        "Four, two from each kidney",
        "One that combines both kidneys",
        "Two, one for each kidney",
        "Three, two from the left kidney and one from the right",
      ],
      correct: 2,
      explanation: "There are typically two renal veins, one for each kidney.",
    },
    {
      type: "question",
      text: "What vein comes before the renal vein?",
      options: [
        "Inferior vena cava",
        "Interlobar veins",
        "Segmental veins",
        "Interlobular veins",
      ],
      correct: 2,
      image: "/images/kidney/kidneyveins1.png",
      explanation:
        "From the nephrons the blood is collected by the capillaries and then the condense into the interlobular veins, then into the arcuate veins, then into the interlobar veins, then into the segmental veins and finally into the renal vein.",
      explanationImage: "/images/kidney/kidneyveins.png",
    },
    {
      type: "text",
      content: `<h3>Lymphatic drainage</h3>
      <p>There are two lymphatic drainage systems in the kidney. </p>
      <h3>The superficial system - capsular lymphatic plexus</h3>
      <p> The <b class="purple-term">capsular lymphatic plexus</b> lies immediately beneath the renal capsule and is
       responsible for draining lymph from <b>the outermost layers of the kidney, including the renal capsule and the 
       outer cortex.</b> The lymphatic vessels from this plexus then drain into the <b>renal lymph nodes (hilar lymph nodes)</b>, 
       which are located adjacent to the renal artery and vein. They eventually drain into the lumbar lymph nodes
        (paraaortic lymph nodes) and then into the cysterna chyli and then into the thoracic duct.</p>
      <p>As the capsular lymphatic plexus is located just beneath the renal capsule, it is particularly vulnerable
       to injury during surgical procedures or trauma to the kidney. However, because of its superficial location and
        its connection to the outermost cortical tissue, it can also provide an important route for lymphatic drainage
         from the kidney in cases of obstruction or damage to the deeper lymphatic vessels.</p>
    
      </p>
      
      <h3>The deep system - In the kidney parenchyma</h3>
      <p>The deep lymphatic drainage system lies mainly in the <b class="purple-term">cortex</b> of the kidney (only on rare occations you find lymphatic vessels in 
      the medulla). In the cortex the lymphatic vessels run alongside both arterioles and 
      tubules.
      </p>
      <p>After that they mirror the path of the venous and arterial drainage, meaning that they drain 
      into the 
      <b class="purple-term">interlobular</b> lymphatic vessels, then into the <b class="purple-term"
      >arcuate</b> 
      lymphatic vessels, then into the <b class="purple-term">interlobar</b> lymphatic vessels, then 
      the <b class="purple-term">segmental</b> lymphatic vessels and 
      finally into the 
      <b class="purple-term">renal</b> lymph nodes (hilar lymph nodes). The renal lymph nodes 
      then drain into the <b class="purple-term">lumbar</b> lymph nodes (paraaortic lymph nodes),
       which eventually drain into the cysterna chyli and then into the
        <b>thoracic duct</b>.</p>
      </p>
     
      </br>
      <p>As there is no lymphatic drainage in the medulla (only in rare cases can be found in the outer
      medulla), the lymphatic drainage of the kidney is not 
      efficient in clearing excess fluid from the interstitial space in the medulla, making it
      particularly vulnerable for interstitial edema or venous congestion compared to the cortex. 
      This can contribute to the development of edema in the medulla during conditions such as nephrotic 
      syndrome or 
      acute interstitial nephritis. This is different from other species like for example the pig and
       rabbit, where there is a lymphatic drainage in the medulla (see this article for 
       more info: <em><a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC6426795/" target="_blank" rel="noopener noreferrer">Renal Lymphatics: Anatomy, Physiology, and Clinical Implications</a></em>).</p>
</a></p>
       <div class="highlight-box">
       <ul>
       <li>
       <b>Superficial system</b>: 
       <ul><li>Capsular lymphatic plexus</li> 
        </ul>
        </li>
        <li><b>Deep system</b>: 
        <ul><li>Interlobular lymphatic vessels</li> <li>Arcuate lymphatic vessels</li> <li>Interlobar lymphatic vessels</li> <li>Segmental lymph nodes</li> 
        </ul>
        <li>Both then drain into: <ul><li><b>Renal lymph nodes (hilar lymph nodes)</b></li> <li>Lumbar lymph nodes (paraaortic lymph nodes)</li> <li>Cisterna chyli</li> <li>Thoracic duct</li></ul>
     </li></ul>
      </div>
      `,
      image: "/images/kidney/kidneylymphaticsboth.png",
    },
    {
      type: "question",
      text: "How many lymphatic drainage systems are there in the kidney?",
      options: ["One", "Two", "Three", "Four"],
      correct: 1,
      explanation:
        "There are two lymphatic drainage systems in the kidney: the deep system and the superficial system.",
    },
    {
      type: "question",
      text: "Where do both the deep and superficial lymphatic drainage systems of the kidney eventually combine and drain into?",
      options: [
        "Renal lymph nodes (hilar lymph nodes)",
        "Lumbar lymph nodes (paraaortic lymph nodes)",
        "Thoracic duct",
        "Cisterna chyli",
      ],
      correct: 0,
      explanation:
        "Both the deep and superficial lymphatic drainage systems of the kidney eventually drain into the renal lymph nodes (hilar lymph nodes), thereafter into the lumbar lymph nodes (paraaortic lymph nodes), then into the cisterna chyli, and finally into the thoracic duct.",
      explanationImage: "/images/kidney/kidneylymphsystem.png",
    },
    {
      type: "flashcard",
      front:
        "What does the deep lymphatic drainage system of the kidney consist of?",
      back: `<ul><b>The Deep Lymphatic Drainage System of the Kidney:</b>
      
      <li>Lies mainly in the <b>cortex</b>, where lymphatic vessels run alongside both arterioles and tubules</li>
      <li>Then drain into the <b>interlobular</b> lymphatic vessels, <b>arcuate</b> lymphatic vessels, <b>interlobar</b> lymphatic vessels, <b>segmental</b> lymphatic vessels, and finally into the <b>renal</b> lymph nodes (hilar lymph nodes)</li>
      <li>The renal lymph nodes then drain into the <b>lumbar</b> lymph nodes (paraaortic lymph nodes) and eventually into the <b>thoracic duct</b></li>
      </ul>
      `,
      hint: "Where does it start and where does it end", // optional
      frontImage: "/images/kidney/kidneylymph2.png", // optional
      backImage: "/images/kidney/kidneylymph1.png", // optional
    },
    {
      type: "flashcard",
      front:
        "Where do you find the superficial lymphatic drainage system in the kidney and what does it drain?",
      back: `<ul>lies immediately <b>beneath the renal capsule</b> and is
       responsible for draining lymph from the outermost layers of the kidney, including <b>the renal capsule and the 
       outer cortex</b></ul>
  `,
      hint: "Think about the name of the system", // optional
      backImage: "/images/kidney/kidneylymph3.png", // optional
    },
    {
      type: "text",
      content: `<h3>Innervation</h3>
      <p>The kidneys receive innervation from the <b class="purple-term">renal plexus</b>, 
      a network of autonomic nerves that surround the renal arteries and veins at the hilum
       of the kidney. </p>
       The origin of the renal plexus is complex and involves contributions from several different sources, including:
      <ul> <li><b>Celiac plexus</b>: a network of nerves located in the upper abdomen, around the aorta, particularly around the origin of the celiac artery. It transmits visceral sensory information (pain, reflexes etc) from the abdominal organs.</li>
       <li><b>Aorticorenal ganglia</b>: a paired prevertebral sympathetic nerve clusters located near the origin of the renal arteries and the abdominal aorta.</li>
       <li><b>Lumbar splanchnic nerves</b>: nerves that arise from the lumbar region of the spinal cord and contribute to the innervation of the abdominal organs.</li>
       </ul>
      <p>Through the renal plexus, the kidneys receive:
      <ul>
      <li><b>Sympathetic (efferent)</b> fibers that originate from the thoracic spinal cord (T10-L2) and are responsible for regulating blood flow to the kidneys, as well as controlling the release of hormones such as renin.</li>
      <li><b>Parasympathetic (efferent)</b> fibers that originate from the vagus nerve and are involved in regulating renal function, including urine production and electrolyte balance.</li>
      <li><b>Sensory innervation</b> that transmits pain and other sensory information from the kidneys to the central nervous system. This is important for the perception of pain in conditions such as kidney stones or pyelonephritis.</li>
      </ul>
      </p>
       
      <h3>The pain innervation of the kidneys</h3>
      <p>The sensory innervation is most dense in the <b>renal pelvis</b> (where the urine collects before draining into the ureter). 
      Meaning that if for example a blockage of the ureter, it will cause distension of the renal pelvis and thus cause pain. 
      But because how the nerves are routed (the sensory fibers from the kidneys enter the spinal cord at the same level as the 
      sensory fibers from the skin of the flank and abdomen), the pain is often referred to the flank or abdomen, rather than being 
      localized to the kidney itself.
      </p>
      <p>There is also some sensory innervation in the <b>renal capsule</b> and the <b>outer cortex</b>, but it is less dense than in the renal pelvis,
       meaning that conditions that cause distension of the renal capsule (for example a tumor or a hemorrhage) can also cause pain, 
       but it is usually less severe than the pain caused by distension of the renal pelvis.
      </p>
      <p>In the <b>renal medulla</b> there is very little sensory innervation, meaning that conditions that cause distension of the medulla (for example a tumor or a hemorrhage) usually do not cause pain.</p>
 <p>This means in clinical practice, that conditions that cause distension of the renal pelvis (for example a blockage of the ureter) 
 usually cause more severe pain than conditions that cause distension of the renal capsule (for example a tumor or a hemorrhage) 
 and that conditions that cause distension of the renal medulla (for example a tumor or a hemorrhage) usually do not cause pain.
 </p>


      <h3>Blood pressure regulation</h3>
      <p>Is done through three main sensory mechanisms: 
      <ul><li>the baroreceptors and chemoreceptors (in the carotid sinus in the neck and aortic arch in the chest)</li>
      <li>the juxtaglomerular apparatus (an internal kidney sensor that checks blood pressure/flow and sodium concentration)</li>
         <li>the <b>sympathetic nerves</b> (located in the thoracic spinal cord, T10-L2)
         <ul><li>When blood pressure is low, the sympathetic nerves stimulate the release of renin from the juxtaglomerular apparatus, which leads to the activation of the renin-angiotensin-aldosterone system (RAAS) and an increase in blood pressure.
         As well as increasing heart rate and constricting blood vessels.</li>
         </ul></li>
      </ul>
      <p>In some cases, such as in patients with resistant hypertension, a procedure called <b class="glossary-term">renal denervation</b> can be performed, which involves disrupting the sympathetic nerves to the kidneys in order to reduce blood pressure. This procedure is still being studied and is not yet widely used in clinical practice.</p>
      `,
      image: "/images/heart/blood-flow.jpg",
    },
    {
      type: "question",
      text: "What is the name of the nerve plexus that innervates the kidneys?",
      options: [
        "Renal plexus",
        "Celiac plexus",
        "Aorticorenal ganglia",
        "Lumbar splanchnic nerves",
      ],
      correct: 0,
      explanation:
        "The renal plexus is the network of autonomic nerves that innervates the kidneys.",
    },
    {
      type: "question",
      text: "What is the primary function of the renal plexus?",
      options: [
        "Regulate blood flow to the kidneys",
        "Control the release of hormones such as renin",
        "Transmit pain and other sensory information from the kidneys",
        "All of the above",
      ],
      correct: 3,
      explanation:
        "The renal plexus is responsible for regulating blood flow to the kidneys, controlling the release of hormones such as renin, and transmitting pain and other sensory information from the kidneys.",
    },
    {
      type: "question",
      text: "Which of the following structures contribute to the formation of the renal plexus?",
      options: [
        "Celiac plexus",
        "Aorticorenal ganglia",
        "Lumbar splanchnic nerves",
        "All of the above",
      ],
      correct: 3,
      explanation:
        "The renal plexus is formed by contributions from the celiac plexus, aorticorenal ganglia, and lumbar splanchnic nerves.",
    },
    {
      type: "question",
      text: "Which types of nerve fibers are present in the renal plexus?",
      options: [
        "Sympathetic only",
        "Parasympathetic only",
        "Both sympathetic and parasympathetic",
        "Neither sympathetic nor parasympathetic",
      ],
      correct: 2,
      explanation:
        "The renal plexus contains both sympathetic and parasympathetic nerve fibers, which regulate various functions of the kidneys.",
    },
    {
      type: "question",
      text: "Where is the renal plexus located?",
      options: [
        "Around the renal arteries and veins at the hilum of the kidney",
        "In the renal capsule",
        "In the outermost cortical tissue",
        "In the intraparenchymal tissue",
      ],
      correct: 0,
      explanation:
        "The renal plexus is located around the renal arteries and veins at the hilum of the kidney.",
    },
    {
      type: "flashcard",
      front: "What nerves makes up the renal plexus?",
      back: "The renal plexus is primarily derived from the celiac plexus, the aorticorenal ganglia, and the lumbar splanchnic nerves.",
      hint: "Three main contributors", // optional
      frontImage: "/images/...", // optional
      backImage: "/images/...", // optional
    },
    {
      type: "text",
      content: `<h3>Innervation</h3>
      `,
      image: "/images/heart/blood-flow.jpg",
    },
    {
      type: "question",
      text: "?",
      options: ["2", "3", "4", "5"],
      correct: 2,
      explanation: ".",
    },
    {
      type: "question",
      text: "?",
      options: ["2", "3", "4", "5"],
      correct: 2,
      explanation: ".",
    },
  ],
};
